import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

export default function AutovaerkstedBillundPage({ settings }) {
  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'

  return (
    <>
      <MetaTags
        title="Autoværksted nær Billund. Reparation & service af biler. Kontakt os"
        description="Vi tilbyder reparation, service & andet på vores autoværksted i Billund. Vores dygtige mekanikere står klar til at hjælpe. Få et tilbud."
        ogTitle="Autoværksted nær Billund. Reparation & service af biler. Kontakt os"
        ogDescription="Vi tilbyder reparation, service & andet på vores autoværksted i Billund. Vores dygtige mekanikere står klar til at hjælpe. Få et tilbud."
        ogImage={imageUrl('img-7698-min-scaled.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* Hero Header */}
      <section className="bg-[#14171a] text-white py-14 sm:py-20 border-b border-[#262c33] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={imageUrl('img-7698-min-scaled.jpg', 'width=1600,quality=80,format=auto')}
            alt="Autoværksted nær Billund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <Link to="/" className="hover:text-white">Forside</Link>
              <span>/</span>
              <span className="text-[#e20019]">Områder</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Autoværksted nær Billund
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              Professionel bilreparation, serviceeftersyn og dækskift i Hejnsvig – kun få minutters
              kørsel fra Billund. Seks autolifte, speciale i VAG-gruppen og gratis lånebil.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book tid til værkstedet</span>
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#20252c] hover:bg-[#2b323c] border border-gray-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                <span>Ring på {phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content from Scrape */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-8 text-gray-800 leading-relaxed text-base">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Autoværksted nær Billund med hjælp til hele bilen
                </h2>
                <p>
                  Har du brug for et autoværksted nær Billund, hvor du kan få bilen undersøgt,
                  serviceret eller repareret? Hos EK Biler i Hejnsvig hjælper vi med en bred vifte af
                  værkstedsopgaver for både nye og ældre biler. Du kan få hjælp, uanset hvilket
                  bilmærke eller hvilken model du kører i, og vi arbejder også med elbiler og
                  hybrider.
                </p>
                <p className="mt-3">
                  Vi ved, at bilen ofte spiller en vigtig rolle i hverdagen. Derfor lægger vi vægt på at
                  undersøge problemet grundigt og give dig en forståelig forklaring på det arbejde, der
                  er relevant. Du får dermed et bedre grundlag for at tage stilling til opgaven, inden
                  reparationen bliver udført.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Autoværksted til alle bilmærker og modeller
                </h2>
                <p>
                  Vores værkstedsafdeling er alsidig og udstyret med seks autolifte samt det nødvendige
                  udstyr til en lang række opgaver. Selvom vi har særlig erfaring med VAG-gruppens biler
                  – Volkswagen, Audi, SEAT og Škoda – er værkstedet ikke begrænset til disse mærker.
                </p>
                <ul className="list-disc list-inside space-y-2 mt-4 pl-2 text-gray-700">
                  <li>Almindelige reparationer</li>
                  <li>Serviceeftersyn efter bilproducentens forskrifter</li>
                  <li>Fejlsøgning og diagnoseudlæsning</li>
                  <li>Kontrol og reparation af bremser</li>
                  <li>Klargøring til syn</li>
                  <li>Airconditionservice</li>
                  <li>Firehjulsudmåling</li>
                  <li>Forsikringsskader og skader på bilruder</li>
                  <li>
                    <Link to="/daekskift" className="text-[#e20019] font-bold hover:underline">
                      Dækskift
                    </Link>
                    , afbalancering og{' '}
                    <Link to="/daekhotel" className="text-[#e20019] font-bold hover:underline">
                      dækopbevaring
                    </Link>
                  </li>
                  <li>
                    <Link to="/elbil-service" className="text-[#e20019] font-bold hover:underline">
                      Service af elbiler og hybridbiler
                    </Link>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-gray-700">
                  Ved at følge fabrikantens forskrifter kan vi servicere nyere biler, uden at
                  fabriksgarantien nødvendigvis bortfalder. Du kan derfor få udført det planlagte
                  serviceeftersyn på et lokalt, uautoriseret værksted og samtidig få arbejdet
                  dokumenteret efter de relevante anvisninger.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Lokalt værksted med et større netværk bag sig
                </h2>
                <p>
                  Siden januar 2023 har EK Biler været en del af Din Bil Partner. Det betyder, at vi
                  arbejder ud fra konceptets principper om kompetent vejledning og ordentligt
                  håndværk, samtidig med at du får den personlige kontakt, der følger med et lokalt
                  værksted.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Få undersøgt fejl og advarselslamper
                </h2>
                <p>
                  En advarselslampe i instrumentbrættet bør undersøges, selvom bilen fortsat kan køre.
                  Lampen kan skyldes alt fra en mindre fejl til et problem, der kan udvikle sig og føre
                  til en større reparation.
                </p>
                <p className="mt-2">
                  På værkstedet kan vi foretage en diagnoseudlæsning og kombinere resultatet med en
                  praktisk gennemgang af bilen. Det hjælper os med at lokalisere fejlen og vurdere, hvad
                  der bør laves. Du får en forklaring på opgaven, så du ved, hvorfor en bestemt
                  reparation anbefales.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Hold hverdagen kørende med en gratis lånebil
                </h2>
                <p>
                  Når din bil skal blive på værkstedet, kan transporten hurtigt blive en udfordring.
                  Derfor kan du få en gratis lånebil, indtil din egen bil er køreklar igen. Du kan
                  således fortsætte din hverdag, mens vi arbejder på bilen.
                </p>
                <p className="mt-2 font-medium text-gray-900">
                  Vi tager 595 kr. ekskl. moms i timen. Vi giver altid et estimat, inden vi går i gang.
                </p>
              </div>

              {/* FAQs from Scrape */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Ofte stillede spørgsmål om autoværkstedet
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Kan I hjælpe, hvis en advarselslampe lyser?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, vi kan foretage diagnoseudlæsning og undersøge, hvad der har udløst
                      advarselslampen. Herefter forklarer vi, hvad fejlen skyldes, og hvilken løsning
                      der er relevant.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad koster en reparation af min bil?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Vi tager 595 kr. ekskl. moms i timen. Vi giver altid et estimat, inden vi går i
                      gang. Det afhænger nemlig af bilens model, den konkrete skade eller fejl, opgavens
                      omfang, tidsforbruget og de reservedele, der skal anvendes.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Kan jeg få opbevaret mine sommer- eller vinterdæk?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, du kan få dækkene opbevaret, indtil de igen skal monteres. Det sparer plads
                      derhjemme og betyder, at dækkene er samlet samme sted, når det er tid til næste
                      skift.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Har I en lånebil, mens min bil bliver repareret?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ja, du kan få en gratis lånebil, mens din bil er på værksted. På den måde kan du
                      fortsat komme omkring, indtil din egen bil er klar.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Booking Card & Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#14171a] text-white p-6 sm:p-8 rounded-2xl border border-gray-800 space-y-4">
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-wider">
                  Nær Billund
                </div>
                <h3 className="text-2xl font-bold">Bestil værkstedstid</h3>
                <p className="text-sm text-gray-300">
                  Vi ligger i Hejnsvig kun ca. 10 minutters kørsel fra Billund.
                </p>
                <div className="pt-2">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white py-3.5 px-6 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book tid online nu</span>
                  </a>
                </div>
              </div>

              <ContactForm
                title="Send en forespørgsel"
                subtitle="Vi vender tilbage for at fuldføre din booking"
                bookingUrl={bookingUrl}
              />
            </div>
          </div>
        </div>
      </section>

      <DinBilpartnerBadge />
      <ReviewsSlider />
    </>
  )
}
